<template>
  <modal :is-active="true" @close="hide" :title="t('configurator.modals.titles.edit_print_article')">
    <template #body>
      <div class="form-horizontal">
        <div class="form-group">
          <label for="booth_refinement" class="control-label">
            {{ t('configurator.form.labels.current_print_article') }}
          </label>
          <div class="row">
            <div class="col-sm-12">
              <p style="padding-bottom: 0;">{{ currentPrintArticle }}</p>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="booth_refinement" class="control-label">
            {{ t('configurator.form.labels.select_print_article') }}<span>*</span>:
          </label>

          <div class="row">
            <div v-if="bookablePrintArticles.length === 0" class="col-sm-12">
              <p style="padding-bottom: 0;">
                {{ t('configurator.labels.no_alternative_print_articles') }}
              </p>
            </div>

            <div v-else class="col-sm-12">
              <multiselect v-model="form.article"
                :placeholder="t('configurator.form.placeholders.please_select')"
                label="name"
                track-by="id"
                :options="bookablePrintArticles"
                :option-height="104"
                :custom-label="customLabel"
                :show-labels="false">
                <template #singleLabel="props">
                  <span class="booth-draft__font">{{ !inEnglish ? props.option.article.name : props.option.article.name_en }}</span>
                </template>
                <template #option="props">
                  <booth-refinement-print-article-option
                    :article="props.option.article">
                  </booth-refinement-print-article-option>
                </template>
                <template #noOptions>
                  {{ t('configurator.labels.no_alternative_print_articles') }}
                </template>
              </multiselect>
              <p v-if="form.errors.has('article')" class="text-danger">
                {{ $t('configurator.form.errors.print_article') }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="form.article && form.article.article.type === ARTICLE_TYPE_LETTERING" class="form-group">
          <label for="label" class="control-label">
            {{ $t('configurator.form.labels.lettering') }}:
          </label>

          <div class="row">
            <div class="col-sm-12">
              <input
                id="label"
                key="input-label"
                class="form-control input-sm"
                type="text"
                name="label"
                v-model.trim="form.label">
              <p v-if="form.errors.has('label')" class="text-danger">
                {{ $t('configurator.form.errors.label') }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="hasLetteringArticle" class="form-group">
          <div class="row">
            <div class="col-sm-6" style="font-size: 16px; font-weight: 400;">
              {{ $t('configurator.labels.characters_inclusive') }}
            </div>
            <div class="col-sm-6 text-right">
              {{ mainStore.tmp.characters_inclusive }}
            </div>
            
            <div class="col-sm-6" style="font-size: 16px; font-weight: 400;">
              {{ $t('configurator.tables.columns.price_per_piece')}}
            </div>
            <div class="col-sm-6 text-right">
              {{ $filters.formatMoney(articlePrice) }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="row">
            <div class="col-sm-6" style="font-size: 16px; font-weight: 400;">
              {{ $t('configurator.labels.price_current') }} <span v-if="isGroup">{{ $t('configurator.form.labels.per_surface') }}</span>
            </div>
            <div class="col-sm-6 text-right">
              {{ $filters.formatMoney(currentPrice) }} 
            </div>
            
            <div v-if="form.article !== null" class="col-sm-6" style="font-size: 16px; font-weight: 400;">
              {{ $t('configurator.labels.price_new') }} <span v-if="isGroup">{{ $t('configurator.form.labels.per_surface') }}</span>
            </div>
            <div v-if="form.article !== null" class="col-sm-6 text-right" :class="{'text-danger': (newPrice > currentPrice)}">
              {{ $filters.formatMoney(newPrice) }}
            </div>
          </div>
        </div>

        <div class="form-group text-center">
          <button type="button" class="btn btn-warning mr-2" @click.prevent="hide">
            {{ $t('configurator.buttons.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click.prevent="submit"
            :disabled="bookablePrintArticles.length === 0">
            {{ $t('configurator.buttons.update') }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup>
import { useCloudStore } from '@/stores/cloud'
import { useMainStore } from '@/store'
import { useI18n } from 'vue-i18n'
import { computed, reactive } from 'vue'
import Form from '@/classes/Form'
import Multiselect from 'vue-multiselect'
import { ARTICLE_TYPE_LETTERING } from '@/constants'
import { showErrorToast, showLoadingIndicator, showSuccessToast } from '@/utils/toast'

const { t } = useI18n({ useScope: 'global' })
const mainStore = useMainStore()
const cloudStore = useCloudStore()
// define props
const props = defineProps({
  refinement: {
    type: Object,
    required: true,
  },
  isSeamlessPrint: {
    type: Boolean,
    default: false,
  },
  isGroup: {
    type: Boolean,
    default: false
  }
})
// define emits
const emit = defineEmits(['hide'])
// define reactive props
const form = reactive(new Form({
  article: null,
  label: '',
}))
// define computed props
const inEnglish = computed(() => mainStore.inEnglish)

const currentPrintArticle = computed(() => {
  return (inEnglish.value)
    ? props.refinement.article.name_en
    : props.refinement.article.name
})

const currentPrice = computed(() => {
  const price = parseFloat(cloudStore.data.exhibition_prices[props.refinement.article_id].price_net ?? '')
  if (props.refinement.article.type === ARTICLE_TYPE_LETTERING) {
    const amount = parseInt(props.refinement.amount) - parseInt(props.refinement.inclusive)

    return amount * price  
  }

  return price
})

const newPrice = computed(() => {
  if (form.article === null) {
    return 0
  }

  const price = parseFloat(cloudStore.data.exhibition_prices[form.article.article_id].price_net ?? '')
  if (form.article.article.type === ARTICLE_TYPE_LETTERING) {
    let inclusive = 0
    if (form.label.trim().length >= mainStore.tmp.characters_inclusive) {
      inclusive = mainStore.tmp.characters_inclusive
    } else {
      inclusive = form.label.trim().length
    }
    const amount = form.label.trim().length - inclusive

    return amount * price
  }

  return price
})

const availablePrintArticles = computed(() => {
  return cloudStore.boothTypeRefinements
    .filter((boothTypeRefinement) => {
      return props.refinement.booth_refinement_id === boothTypeRefinement.id
    })
    .flat()
    .pop()
    .articles
    .filter((article) => {
      const isSeamlessPrintAsNumber = (props.isSeamlessPrint) ? 1 : 0
      return article.article_id !== props.refinement.article_id
        && article.is_seamless_print === isSeamlessPrintAsNumber
    })
})

const bookablePrintArticles = computed(() => {
  return availablePrintArticles.value
    .filter((availableRefinement) => {
      return mainStore.availability.bookable_article_ids.includes(availableRefinement.article_id)
        && !mainStore.availability.disabled_booth_refinement_ids.includes(availableRefinement.booth_refinement_id)
    })
})

const hasLetteringArticle = computed(() => {
  return props.refinement.article.type === ARTICLE_TYPE_LETTERING
    || (form.article && form.article.article.type === ARTICLE_TYPE_LETTERING)
})

const articlePrice = computed(() => {
  if (props.refinement.article.type === ARTICLE_TYPE_LETTERING) {
    return parseFloat(cloudStore.data.exhibition_prices[props.refinement.article_id].price_net)
  } else if (form.article && form.article.article.type === ARTICLE_TYPE_LETTERING) {
    return parseFloat(cloudStore.data.exhibition_prices[form.article.article_id].price_net)
  }

  return 0
})
// define methods
const hide = () => {
  emit('hide')
}

const submit = async () => {
  showLoadingIndicator(
    t('configurator.indicators.update_print_article'),
    t('configurator.indicators.one_moment_please')
  )
  await cloudStore.changeBoothPrintArticle(form, props.refinement.id)
    .then(() => {
      showSuccessToast(t('configurator.toasts.messages.print_article_updated'), t('toast.title.success'))
      hide()
    })
    .catch(() => showErrorToast(t('configurator.toasts.errors.print_article_updated'), t('toast.title.error')))
}

const customLabel = ({name, description}) => {
  return '${title} - ${description}'
}
</script>